import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import LoginView from '@/views/LoginView.vue'
import DashboardView from '@/views/DashboardView.vue'
import UsersView from '@/views/UsersView.vue'
import CreateUserView from '@/views/CreateUserView.vue'
import EditUserView from '@/views/EditUserView.vue'
import ImportUsersView from '@/views/ImportUsersView.vue'
import StatisticsView from '@/views/StatisticsView.vue'
import KeycloakView from '@/views/KeycloakView.vue'
import UnauthorizedView from '@/views/UnauthorizedView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import AuthCallbackView from '@/views/AuthCallbackView.vue'
import KeycloakInit from '@/components/KeycloakInit.vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresAuth: false }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallbackView,
    meta: { requiresAuth: false }
  },
  {
    path: '/init',
    name: 'KeycloakInit',
    component: KeycloakInit,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    redirect: () => {
      // Redirigir a /init para inicializar Keycloak
      return '/init'
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/users',
    name: 'Users',
    component: UsersView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/users/create',
    name: 'CreateUser',
    component: CreateUserView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/users/:id/edit',
    name: 'EditUser',
    component: EditUserView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/users/import',
    name: 'ImportUsers',
    component: ImportUsersView,
    meta: { requiresAuth: true }
  },
  {
    path: '/statistics',
    name: 'Statistics',
    component: () => import('@/views/StatisticsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/keycloak',
    name: 'Keycloak',
    component: () => import('@/views/KeycloakView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: UnauthorizedView,
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: { requiresAuth: false }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes: routes as RouteRecordRaw[]
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  console.log(`🔄 Router: Navegando de ${from.path} a ${to.path}`)
  
  // Rutas públicas que no requieren autenticación
  const publicRoutes = ['/login', '/auth/callback', '/init', '/unauthorized', '/404']
  if (publicRoutes.includes(to.path) || !to.meta.requiresAuth) {
    return next()
  }
  
  // Para rutas protegidas, verificar autenticación
  if (to.meta.requiresAuth) {
    try {
      // Verificar si ya está autenticado
      if (authStore.isAuthenticated && authStore.user) {
        console.log('✅ Router: Usuario ya autenticado')
        
        // Verificar rol de administrador si es requerido
        if (to.meta.requiresAdmin && !authStore.isAdmin) {
          console.warn('⚠️ Router: Usuario sin permisos de administrador')
          return next('/unauthorized')
        }
        
        return next()
      }
      
      // Intentar verificar autenticación
      console.log('🔍 Router: Verificando autenticación...')
      const isAuthenticated = await authStore.checkAuth()
      
      if (isAuthenticated && authStore.user) {
        console.log('✅ Router: Autenticación verificada')
        
        // Verificar rol de administrador si es requerido
        if (to.meta.requiresAdmin && !authStore.isAdmin) {
          console.warn('⚠️ Router: Usuario sin permisos de administrador')
          return next('/unauthorized')
        }
        
        return next()
      }
      
      // No autenticado, redirigir al login
      console.log('❌ Router: Usuario no autenticado, redirigiendo al login')
      return next('/login')
      
    } catch (error) {
      console.error('❌ Router: Error verificando autenticación:', error)
      return next('/login')
    }
  }
  
  // Ruta no requiere autenticación
  next()
})

export default router
